import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealText, RevealLines } from "@/components/RevealText";
import { Marquee } from "@/components/Marquee";
import { EnquiryForm } from "@/components/EnquiryForm";
import { PRODUCTS } from "@/data/products";
import { INDUSTRIES, SERVICES, PROCESS, USPS, openWhatsApp } from "@/data/site";
import { FAQS } from "@/data/faq";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowUpRight, Plus, MessageCircle, MapPin } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1772300704502-410f0fbd43bb?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";
const MANIFESTO_IMG_1 = "https://images.unsplash.com/photo-1771970472779-6452fbce94d6?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const MANIFESTO_IMG_2 = "https://images.unsplash.com/photo-1784739893954-b64adc267d62?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
    const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

    return (
        <div data-testid="home-page">
            {/* ============ HERO ============ */}
            <section
                ref={heroRef}
                className="relative min-h-screen w-full overflow-hidden bg-ink-950 flex flex-col justify-end pb-16 pt-32"
                data-testid="hero-section"
            >
                {/* Parallax hero image */}
                <motion.div
                    style={{ y: heroImgY }}
                    className="absolute inset-0 -top-[20%] h-[130%]"
                >
                    <img
                        src={HERO_IMG}
                        alt="Modern industrial warehouse interior"
                        className="w-full h-full object-cover opacity-45"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink-950/70 via-ink-950/40 to-ink-950" />
                    <div className="absolute inset-0 blueprint-grid opacity-40" />
                </motion.div>

                {/* Top overline */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="absolute top-32 left-6 lg:left-12 flex items-center gap-4 z-10"
                >
                    <div className="h-px w-12 bg-edge" />
                    <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">
                        Est. Bihar · Authorized Dealer & Solutions Provider
                    </span>
                </motion.div>

                {/* Right meta */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5, duration: 0.8 }}
                    className="absolute top-32 right-6 lg:right-12 hidden md:block text-right z-10"
                >
                    <div className="text-[10px] tracking-[0.3em] text-slateink uppercase">Coverage</div>
                    <div className="font-display font-bold text-lg mt-1">Bihar → Pan India</div>
                    <div className="mt-6 text-[10px] tracking-[0.3em] text-slateink uppercase">Products</div>
                    <div className="font-display font-bold text-lg mt-1">04 categories</div>
                </motion.div>

                <motion.div style={{ y: heroTextY, opacity: heroOpacity }} className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 w-full">
                    <RevealLines
                        as="h1"
                        lines={["Smart", "Insulation.", <>Strong <span className="text-edge">Protection.</span></>]}
                        className="font-display font-black uppercase tracking-tighter text-[15vw] md:text-[13vw] lg:text-[11vw] leading-[0.88]"
                        testid="hero-headline"
                    />

                    <div className="mt-12 grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-6">
                            <RevealText
                                text="Premium reflective foil, XLPE and packaging insulation — engineered specification, installed by our own crews, backed by post-monsoon inspection."
                                className="font-sans text-base md:text-lg text-white/80 max-w-xl leading-relaxed"
                                delay={1.4}
                                stagger={0.02}
                            />
                        </div>
                        <div className="lg:col-span-6 flex flex-col sm:flex-row gap-4 lg:justify-end">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.9, duration: 0.6 }}
                            >
                                <Link
                                    to="/contact"
                                    className="group inline-flex items-center gap-2 bg-edge text-ink-950 font-bold text-sm px-8 py-5 uppercase tracking-wider hover:bg-edge-hover transition-colors"
                                    data-testid="hero-cta-quote"
                                >
                                    Get a Free Quote
                                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </Link>
                            </motion.div>
                            <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 2, duration: 0.6 }}
                                onClick={() => openWhatsApp()}
                                className="inline-flex items-center gap-2 border border-white/30 hover:border-edge text-white font-bold text-sm px-8 py-5 uppercase tracking-wider transition-colors"
                                data-testid="hero-cta-whatsapp"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp Us
                            </motion.button>
                        </div>
                    </div>
                </motion.div>

                {/* Bottom stat strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2, duration: 0.8 }}
                    className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 w-full mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/10 pt-8"
                >
                    {[
                        ["97%", "Radiant Heat Blocked"],
                        ["25+ Yrs", "Material Lifespan"],
                        ["8-15°C", "Indoor Temp Drop"],
                        ["48 Hrs", "Quotation Turnaround"],
                    ].map(([n, l], i) => (
                        <div key={i}>
                            <div className="font-display text-3xl md:text-4xl font-black text-white">{n}</div>
                            <div className="text-[10px] tracking-[0.25em] text-slateink uppercase mt-1">{l}</div>
                        </div>
                    ))}
                </motion.div>
            </section>

            {/* ============ MARQUEE ============ */}
            <Marquee items={["Air Bubble", "XLPE", "Foil Tape", "Packaging Roll", "Site Inspection"]} speed="slow" variant="outline" />

            {/* ============ MANIFESTO ============ */}
            <section className="relative py-24 lg:py-32 border-b border-white/10" data-testid="manifesto-section">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                    <div className="flex items-center gap-4 mb-16">
                        <div className="h-px w-12 bg-edge" />
                        <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">The Manifesto</span>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 mb-24">
                        <div className="lg:col-span-4 relative">
                            <div className="font-editorial italic text-[12vw] lg:text-[10vw] leading-none text-edge/80">01</div>
                        </div>
                        <div className="lg:col-span-7 lg:col-start-6">
                            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                                We don't sell rolls.
                                <br />
                                <span className="text-slateink">We sell a working roof.</span>
                            </h2>
                            <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
                                Anyone can drop material on a site. We specify, measure, install, tape every joint, and come back after the first monsoon to inspect what we built. If the roof fails, we own it.
                            </p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 mb-24 items-center">
                        <div className="lg:col-span-6">
                            <div className="relative overflow-hidden aspect-[4/5] bg-ink-800">
                                <motion.img
                                    initial={{ scale: 1.1 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
                                    viewport={{ once: true }}
                                    src={MANIFESTO_IMG_1}
                                    alt="Industrial ceiling"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
                                <div className="absolute top-6 left-6 flex items-center gap-3">
                                    <div className="w-2 h-8 bg-edge" />
                                    <span className="text-[10px] tracking-[0.3em] text-white uppercase">Spec. Grade</span>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-5 lg:col-start-8">
                            <div className="font-editorial italic text-[10vw] lg:text-[7vw] leading-none text-white/10">02</div>
                            <h3 className="font-display uppercase text-3xl md:text-4xl font-black mt-4 tracking-tighter">
                                Material that outlives the building.
                            </h3>
                            <p className="mt-6 text-white/70 leading-relaxed">
                                Pure aluminium foil, sealed bubble cores, closed-cell XLPE — no metallised polyester pretending to be foil. Every roll comes with a certificate you can hand to your architect.
                            </p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-5">
                            <div className="font-editorial italic text-[10vw] lg:text-[7vw] leading-none text-white/10">03</div>
                            <h3 className="font-display uppercase text-3xl md:text-4xl font-black mt-4 tracking-tighter">
                                Our people are on the roof, not on the phone.
                            </h3>
                            <p className="mt-6 text-white/70 leading-relaxed">
                                Every installation is executed by a Thermo Edge crew or an audited partner. No third-hand contracting, no runaway costs, no "we'll send someone next week".
                            </p>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7">
                            <div className="relative overflow-hidden aspect-[4/3] bg-ink-800">
                                <motion.img
                                    initial={{ scale: 1.1 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
                                    viewport={{ once: true }}
                                    src={MANIFESTO_IMG_2}
                                    alt="Foil texture close up"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-b from-ink-950/40 via-transparent to-ink-950/60" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ PRODUCTS ============ */}
            <section className="py-24 lg:py-32 border-b border-white/10" data-testid="products-section">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                        <div>
                            <div className="flex items-center gap-4 mb-6">
                                <div className="h-px w-12 bg-edge" />
                                <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">The Catalogue</span>
                            </div>
                            <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9]">
                                Four products.
                                <br />
                                <span className="text-slateink">One promise.</span>
                            </h2>
                        </div>
                        <Link to="/products" className="text-sm uppercase tracking-widest hover-underline" data-testid="products-view-all">
                            View all →
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-2 gap-[1px] bg-white/10">
                        {PRODUCTS.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/products/${p.slug}`}
                                className="group relative bg-ink-950 p-8 lg:p-12 spotlight overflow-hidden"
                                onMouseMove={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                                    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                                }}
                                data-testid={`product-card-${p.slug}`}
                            >
                                <div className="relative z-10 grid md:grid-cols-5 gap-6 items-start">
                                    <div className="md:col-span-3">
                                        <div className="font-editorial italic text-6xl text-edge/60">{p.index}</div>
                                        <h3 className="font-display uppercase text-2xl md:text-3xl font-black mt-4 tracking-tight">
                                            {p.name}
                                        </h3>
                                        <p className="mt-4 text-white/70 max-w-md leading-relaxed">{p.short}</p>
                                        <div className="mt-8 inline-flex items-center gap-2 text-edge text-sm uppercase tracking-widest">
                                            Explore <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                        </div>
                                    </div>
                                    <div className="md:col-span-2 relative aspect-square overflow-hidden bg-ink-800">
                                        <img
                                            src={p.thumb}
                                            alt={p.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms] ease-out"
                                        />
                                        <div className="absolute inset-0 border border-white/20" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ INDUSTRIES ============ */}
            <section className="py-24 lg:py-32 border-b border-white/10" data-testid="industries-section">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-5">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="h-px w-12 bg-edge" />
                                <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Industries We Serve</span>
                            </div>
                            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                                Where cooler
                                <br /> means <span className="text-edge">cheaper</span>.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 text-lg text-white/70 leading-relaxed">
                            From poultry sheds losing weight gain to warehouses melting inventory — our clients share one thing: every degree above 32°C is money. We build the reflective barrier that stops it.
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-white/10">
                        {INDUSTRIES.map((ind, i) => (
                            <div
                                key={ind}
                                className="group relative bg-ink-950 p-8 lg:p-10 hover:bg-ink-900 transition-colors cursor-default"
                                data-testid={`industry-${i}`}
                            >
                                <div className="font-editorial italic text-slateink/40 text-lg">{String(i + 1).padStart(2, "0")}</div>
                                <div className="font-display uppercase text-xl md:text-2xl font-bold mt-6 tracking-tight leading-tight">
                                    {ind}
                                </div>
                                <div className="mt-8 h-px w-8 bg-edge group-hover:w-full transition-all duration-700" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ WHY / USPs ============ */}
            <section className="py-24 lg:py-32 border-b border-white/10" data-testid="why-section">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="h-px w-12 bg-edge" />
                        <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Why Thermo Edge</span>
                    </div>
                    <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] max-w-3xl">
                        Ten reasons your architect says our name.
                    </h2>

                    <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-6">
                        {USPS.map((usp, i) => (
                            <motion.div
                                key={usp}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04, duration: 0.5 }}
                                viewport={{ once: true }}
                                className="flex items-start gap-3 border-t border-white/10 pt-6"
                            >
                                <Plus className="w-4 h-4 text-edge mt-1 shrink-0" strokeWidth={2.5} />
                                <span className="font-display font-semibold text-base uppercase tracking-tight">{usp}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ PROCESS ============ */}
            <section className="py-24 lg:py-32 border-b border-white/10" data-testid="process-section">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="h-px w-12 bg-edge" />
                        <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Our Working Process</span>
                    </div>
                    <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] max-w-3xl">
                        Five steps. Zero surprises.
                    </h2>

                    <div className="mt-16 grid md:grid-cols-5 gap-[1px] bg-white/10">
                        {PROCESS.map((s) => (
                            <div key={s.num} className="bg-ink-950 p-8 lg:p-10" data-testid={`process-step-${s.num}`}>
                                <div className="font-editorial italic text-edge text-5xl">{s.num}</div>
                                <div className="font-display uppercase text-xl font-bold mt-6 tracking-tight">{s.title}</div>
                                <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ FAQ ============ */}
            <section className="py-24 lg:py-32 border-b border-white/10" data-testid="home-faq-section">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="h-px w-12 bg-edge" />
                            <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Questions</span>
                        </div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
                            The ones people ask before signing.
                        </h2>
                        <Link to="/faq" className="mt-8 inline-block text-sm uppercase tracking-widest hover-underline">
                            All FAQs →
                        </Link>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <Accordion type="single" collapsible className="w-full">
                            {FAQS.slice(0, 5).map((f, i) => (
                                <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                                    <AccordionTrigger className="text-left font-display uppercase text-base md:text-lg tracking-tight hover:text-edge py-6" data-testid={`home-faq-${i}`}>
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-white/70 pb-6 leading-relaxed">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* ============ CTA / ENQUIRY ============ */}
            <section className="relative py-24 lg:py-32 border-b border-white/10 overflow-hidden" data-testid="home-cta-section">
                <div className="absolute inset-0 blueprint-grid opacity-30" />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="h-px w-12 bg-edge" />
                                <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Start Here</span>
                            </div>
                            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                                Request a<br />free site inspection.
                            </h2>
                            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                                An engineer on your roof within 48 hours. No fee. No commitment. You get a firm written quote — we get a real conversation.
                            </p>
                            <div className="mt-10 flex items-start gap-4 text-sm">
                                <MapPin className="w-5 h-5 text-edge mt-0.5" />
                                <div>
                                    <div className="uppercase tracking-widest text-slateink text-xs">Coverage</div>
                                    <div className="font-display text-lg mt-1">Bihar · Pan-India on request</div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 border border-white/10 bg-ink-900/50 backdrop-blur-sm p-8 lg:p-10">
                            <EnquiryForm variant="full" source="home-cta" />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
